import {
  AmountFormat,
  ApplicationAlert,
  AssetType,
  AuditEntry,
  AuthorisedUser,
  Axe,
  AxeTradeCondition,
  BasisConvention,
  BatchData,
  BestPrice,
  CallableType,
  ClearingHouse,
  ClearingHouseInteraction,
  ClearingHouseType,
  ClearingMessageType,
  ClusterClientState,
  ClusterPong,
  ClusterSession,
  Collateral,
  CollateralBasket,
  CollateralBasketType,
  CollateralDetails,
  CollateralIdentifierType,
  CollateralPrice,
  CollateralType,
  CollateralUpload,
  Collection,
  CollectionType,
  ComplianceCollateral,
  CopyPasteFormat,
  Counter,
  Country,
  CouponFrequency,
  Currency,
  DatabaseReportGatewayRequest,
  DateFormat,
  DateTerm,
  Deal,
  DealExportEntry,
  DealSettlement,
  DealUpdate,
  DefaultCollateralIdentifierType,
  DefaultMarket,
  DevSignal,
  Direction,
  DirectionFormat,
  DurationTerm,
  EgressPosition,
  EntityStatus,
  EoniaRate,
  EstrRate,
  EvergreenNoticePeriod,
  ExDividendDayConvention,
  ExportFailedRetryData,
  ExtendedTradingEntityType,
  FatFingerProductSectorLimit,
  FatFingerUserLimit,
  FxRate,
  HaircutBand,
  HaircutCollateralBand,
  HaircutEntry,
  HaircutSign,
  HaircutType,
  Holiday,
  ImportComplete,
  Index,
  IndexLag,
  InFlightOrganisation,
  InFlightTargetOrganisation,
  Instrument,
  Issue,
  IssuerOrganisation,
  Market,
  MarketType,
  MasterLegalAgreementType,
  MidTradeAction,
  MidTradeResponse,
  Negotiation,
  NegotiationAggregate,
  NegotiationHistory,
  NegotiationOverride,
  NegotiationUpdate,
  Notes,
  Order,
  OrderRecordKeeping,
  OrderTerm,
  Organisation,
  OrganisationMeta,
  OrganisationType,
  PermissionReportRequest,
  Platform,
  PrimeBroker,
  Product,
  ProductSector,
  ProductSectorAggregate,
  ProductSectorMappingDetail,
  ProductSectorMappingDraftDetail,
  ProductSectorMappingState,
  QueryRequest,
  RateType,
  ReferenceDataManagementData,
  ReferenceRate,
  RefinitivCollateralImportResult,
  RelationshipAction,
  RepoClassification,
  RepoType,
  RfqType,
  ServicingType,
  ServicingTypeNewDeal,
  SettlementVenue,
  SotwPosition,
  SotwReset,
  Sponsor,
  SponsorType,
  SpreadsheetCsd,
  SpreadsheetMapping,
  SpreadsheetSettlementVenue,
  StartTimeZone,
  SummaryRowDirection,
  TradeConditionFlag,
  TradingControlLimit,
  TradingControlLimitUserUtilisation,
  TradingEntity,
  TradingEntityClearing,
  TradingEntityMeta,
  TradingEntityProductPermission,
  TradingEntityWithAlias,
  TradingRelationship,
  TradingType,
  User,
  UserPreference,
  UserSettings,
  WorkingDays
} from 'api'
import { Locale, Translation } from 'locale'
import { assertNever, NotificationPermissionEnum } from 'utils'
import { DataHelper } from './DataHelperTypes'
import { isCollateral } from './collateralUtils'

export const AmountFormatHelper: DataHelper<AmountFormat> = {
  getId: (amountFormat: AmountFormat) => amountFormat,
  getName: (amountFormat: AmountFormat) => {
    switch (amountFormat) {
      case AmountFormat.ACTUAL:
        return Locale.translate(Translation.SC_ACTUAL)

      case AmountFormat.THOUSANDS:
        return Locale.translate(Translation.SC_THOUSANDS)

      case AmountFormat.MILLIONS:
        return Locale.translate(Translation.SC_MILLIONS)

      default:
        throw new Error(`Unhandled AmountFormat ${[amountFormat]}`)
    }
  }
}

export const AlertEntityHelper: DataHelper<ApplicationAlert> = {
  getId: (alert: ApplicationAlert) => alert.correlationId,
  getName: (alert: ApplicationAlert) => alert.correlationId
}

export const AxeEntityHelper: DataHelper<Axe> = {
  getId: (axe: Axe) => axe.axeId,
  getName: (axe: Axe) => axe.axeId
}

export const AuditEntryHelper: DataHelper<AuditEntry<any>> = {
  getId: (auditEntry: AuditEntry<any>) => auditEntry.id,
  getName: (auditEntry: AuditEntry<any>) => auditEntry.id
}

export const AuthorisedUserHelper: DataHelper<AuthorisedUser> = {
  getId: (user: AuthorisedUser) => user.id,
  getName: (user: AuthorisedUser) => `${user.firstName} ${user.lastName}`
}

export const AxeHelper: DataHelper<boolean> = {
  getId: (isAxed: boolean) => isAxed.toString(),
  getName: (isAxed: boolean) => Locale.translate(Translation.TC_AXED, { isAxed: !!isAxed })
}

export const AxeTradeConditionHelper: DataHelper<AxeTradeCondition> = {
  getId: (axeTradeCondition: AxeTradeCondition) =>
    `${AxeHelper.getName(axeTradeCondition.isAxed)}, ${TradeConditionFlagHelper.getName(axeTradeCondition.tradeCondition)}`,
  getName: (axeTradeCondition: AxeTradeCondition) =>
    `${AxeHelper.getName(axeTradeCondition.isAxed)}, ${TradeConditionFlagHelper.getName(axeTradeCondition.tradeCondition)}`
}

export const BasisConventionHelper: DataHelper<BasisConvention> = {
  getId: (basisConvention: BasisConvention) => basisConvention,
  getName: (basisConvention: BasisConvention) => basisConvention
}

export const BestPriceHelper: DataHelper<BestPrice> = {
  getId: (bestPrice: BestPrice) => bestPrice.bestPriceId,
  getName: (bestPrice: BestPrice) => bestPrice.bestPriceId
}

export const BatchDataHelper: DataHelper<BatchData> = {
  getId: () => '',
  getName: () => ''
}

export const ClearingHouseHelper: DataHelper<ClearingHouse> = {
  getId: (clearingHouse: ClearingHouse) => clearingHouse.id,
  getName: (clearingHouse: ClearingHouse) => clearingHouse.publishedName
}

export const ClearingHouseInteractionHelper: DataHelper<ClearingHouseInteraction> = {
  getId: (clearingHouseInteraction: ClearingHouseInteraction) => clearingHouseInteraction.id,
  getName: (clearingHouseInteraction: ClearingHouseInteraction) => clearingHouseInteraction.id
}

export const ClearingHouseTypeHelper: DataHelper<ClearingHouseType> = {
  getId: (clearingHouseType: ClearingHouseType) => clearingHouseType,
  getName: (clearingHouseType: ClearingHouseType) => {
    switch (clearingHouseType) {
      case ClearingHouseType.LCH:
        return 'LCH Ltd'

      case ClearingHouseType.LCHSA:
        return 'LCH SA'

      case ClearingHouseType.FICC:
        return 'Fixed Income Clearing Corporation'

      default:
        throw new Error(`Unhandled ClearingHouseType ${clearingHouseType}`)
    }
  }
}

export const ClearingMessageTypeHelper: DataHelper<ClearingMessageType> = {
  getId: (messageType: ClearingMessageType) => messageType,
  getName: (messageType: ClearingMessageType) => {
    switch (messageType) {
      case ClearingMessageType.TRADE_MESSAGE:
        return 'TRADE'
      case ClearingMessageType.TRADE_CANCEL_MESSAGE:
        return 'CANCEL'
      default:
        return messageType
    }
  }
}

export const ClusterSessionHelper: DataHelper<ClusterSession> = {
  getId: () => '',
  getName: () => ''
}

export const ClusterPongHelper: DataHelper<ClusterPong> = {
  getId: () => '',
  getName: () => ''
}

export const ClusterClientStateHelper: DataHelper<ClusterClientState> = {
  getId: () => '',
  getName: () => ''
}

export const EgressPositionHelper: DataHelper<EgressPosition> = {
  getId: () => '',
  getName: () => ''
}

export const CounterHelper: DataHelper<Counter> = {
  getId: () => '',
  getName: () => ''
}

export const CollateralHelper: DataHelper<Collateral> = {
  getId: (collateral: Collateral) => collateral.id,
  getName: (collateral: Collateral) => collateral.name
}

export const CountryHelper: DataHelper<Country> = {
  getId: (country: Country) => country.isoTwoDigitAlphaCode,
  getName: (country: Country) => country.countryName
}

export const CollateralDetailsHelper: DataHelper<CollateralDetails> = {
  getId: (collateralDetails: CollateralDetails) => collateralDetails.id,
  getName: (collateralDetails: CollateralDetails) => collateralDetails.id
}

export const ComplianceCollateralHelper: DataHelper<ComplianceCollateral> = {
  getId: (complianceCollateral: ComplianceCollateral) => complianceCollateral.id,
  getName: (complianceCollateral: ComplianceCollateral) => complianceCollateral.id
}

export const CollateralPriceHelper: DataHelper<CollateralPrice> = {
  getId: (collateralPrice: CollateralPrice) => collateralPrice.collateralId,
  getName: (_collateralPrice: CollateralPrice) => ''
}

export const CollateralUploadHelper: DataHelper<CollateralUpload> = {
  getId: (collateralUpload: CollateralUpload) => collateralUpload.ISIN!!,
  getName: (_collateralUpload: CollateralUpload) => ''
}

export const RefinitivCollateralImportResultHelper: DataHelper<RefinitivCollateralImportResult> = {
  getId: (_refinitivCollateralImportResult: RefinitivCollateralImportResult) => '',
  getName: (_RefinitivCollateralImportResult: RefinitivCollateralImportResult) => ''
}

export const ImportCompleteHelper: DataHelper<ImportComplete> = {
  getId: (importComplete: ImportComplete) => importComplete.filename!!,
  getName: (_importComplete: ImportComplete) => ''
}

export const CollateralBasketHelper: DataHelper<CollateralBasket> = {
  getId: (collateralBasket: CollateralBasket) => collateralBasket.id,
  getName: (collateralBasket: CollateralBasket) => collateralBasket.name
}

export const CollateralBasketTypeHelper: DataHelper<CollateralBasketType> = {
  getId: (collateralBasketType: CollateralBasketType) => collateralBasketType,
  getName: (collateralBasketType: CollateralBasketType) => {
    switch (collateralBasketType) {
      case CollateralBasketType.GC_BESPOKE:
        return Locale.translate(Translation.TC_BASKET_TYPE_GC_BESPOKE)
      case CollateralBasketType.DBV:
        return Locale.translate(Translation.TC_BASKET_TYPE_DBV)
      case CollateralBasketType.EUROCLEAR:
        return Locale.translate(Translation.TC_BASKET_TYPE_EUROCLEAR)
      case CollateralBasketType.BONY:
        return Locale.translate(Translation.TC_BASKET_TYPE_BONY)
      case CollateralBasketType.GC_PLUS:
        return Locale.translate(Translation.TC_BASKET_TYPE_GC_PLUS)
      case CollateralBasketType.CLEAR_STREAM:
        return Locale.translate(Translation.TC_BASKET_TYPE_CLEAR_STREAM)
      case CollateralBasketType.CASH:
        return Locale.translate(Translation.TC_BASKET_TYPE_CASH)
      case CollateralBasketType.COLLATERAL:
        return Locale.translate(Translation.TC_BASKET_TYPE_COLLATERAL)
      default:
        return assertNever(collateralBasketType, `Unhandled Collateral Basket type ${collateralBasketType}`)
    }
  }
}

export const CollateralIdentifierTypeHelper: DataHelper<CollateralIdentifierType> = {
  getId: (collateralIdentifierType: CollateralIdentifierType) => collateralIdentifierType,
  getName: (collateralIdentifierType: CollateralIdentifierType) => {
    switch (collateralIdentifierType) {
      case CollateralIdentifierType.ISIN:
        return Locale.translate(Translation.TC_MARKET_EUROPE)

      case CollateralIdentifierType.CUSIP:
        return Locale.translate(Translation.TC_MARKET_US)

      default:
        throw new Error(`Unhandled Collateral Identifier Type ${[collateralIdentifierType]}`)
    }
  }
}

export const CollateralTypeHelper: DataHelper<CollateralType> = {
  getId: (collateralType: CollateralType) => collateralType,
  getName: (collateralType: CollateralType) => {
    switch (collateralType) {
      case CollateralType.STANDARD:
        return Locale.translate(Translation.TC_COLLATERAL_TYPE_STANDARD)

      case CollateralType.INDEX_LINKED:
        return Locale.translate(Translation.TC_COLLATERAL_TYPE_INDEX_LINKED)

      default:
        throw new Error(`Unhandled Collateral Type ${[CollateralType]}`)
    }
  }
}

export const CollectionHelper: DataHelper<Collection> = {
  getId: (collection: Collection) => collection.id,
  getName: (collection: Collection) => collection.name
}

export const CollectionTypeHelper: DataHelper<CollectionType> = {
  getId: (collectionType: CollectionType) => collectionType,
  getName: (collectionType: CollectionType) => {
    switch (collectionType) {
      case CollectionType.ALL_OR_NOTHING:
        return Locale.translate(Translation.TC_ALL_OR_NOTHING)
      case CollectionType.NON_CONTINGENT:
        return Locale.translate(Translation.TC_NON_CONTINGENT)
      case CollectionType.SEND_INDIVIDUALLY:
        return Locale.translate(Translation.TC_SEND_INDIVIDUALLY)
      case CollectionType.ONE_CANCELS_OTHERS:
        return Locale.translate(Translation.TC_ONE_CANCELS_OTHERS)
      case CollectionType.SMART_GROUPING:
        return Locale.translate(Translation.TC_SMART_GROUPING)
      case CollectionType.ALL_OR_NOTHING_X:
        return Locale.translate(Translation.TC_ALL_OR_NOTHING_X)

      default:
        throw new Error(`Unhandled Collection Type ${[collectionType]}`)
    }
  }
}

export const CollectionTypeAbbreviatedHelper: DataHelper<CollectionType> = {
  getId: (collectionType: CollectionType) => collectionType,
  getName: (collectionType: CollectionType) => {
    switch (collectionType) {
      case CollectionType.ALL_OR_NOTHING:
        return Locale.translate(Translation.TC_ALL_OR_NOTHING)
      case CollectionType.SEND_INDIVIDUALLY:
        return Locale.translate(Translation.TC_SEND_INDIVIDUALLY_ABBR)
      case CollectionType.ONE_CANCELS_OTHERS:
        return Locale.translate(Translation.TC_ONE_CANCELS_OTHERS)
      case CollectionType.SMART_GROUPING:
        return Locale.translate(Translation.TC_SMART_GROUPING)

      default:
        throw new Error(`Unhandled Collection Type ${[collectionType]}`)
    }
  }
}

export const CouponFrequencyHelper: DataHelper<CouponFrequency> = {
  getId: (couponFrequency: CouponFrequency) => couponFrequency,
  getName: (couponFrequency: CouponFrequency) => {
    switch (couponFrequency) {
      case CouponFrequency.ANNUAL:
        return Locale.translate(Translation.TC_ANNUAL)

      case CouponFrequency.BI_ANNUAL:
        return Locale.translate(Translation.TC_SEMI_ANNUAL)

      case CouponFrequency.QUARTERLY:
        return Locale.translate(Translation.TC_QUARTERLY)

      case CouponFrequency.MONTHLY:
        return Locale.translate(Translation.TC_MONTHLY)

      default:
        throw new Error(`Unhandled Coupon Frequency ${[couponFrequency]}`)
    }
  }
}

export const SpreadsheetCsdHelper: DataHelper<DealSettlement> = {
  getId: (csd: DealSettlement) => csd,
  getName: (csd: DealSettlement) => csd
}

export const CurrencyHelper: DataHelper<Currency> = {
  getId: (currency: Currency) => currency,
  getName: (currency: Currency) => {
    switch (currency) {
      case Currency.AUD:
        return Locale.translate(Translation.TC_CURRENCY_AUD)
      case Currency.CAD:
        return Locale.translate(Translation.TC_CURRENCY_CAD)
      case Currency.DKK:
        return Locale.translate(Translation.TC_CURRENCY_DKK)
      case Currency.EUR:
        return Locale.translate(Translation.TC_CURRENCY_EUR)
      case Currency.GBP:
        return Locale.translate(Translation.TC_CURRENCY_GBP)
      case Currency.JPY:
        return Locale.translate(Translation.TC_CURRENCY_JPY)
      case Currency.SEK:
        return Locale.translate(Translation.TC_CURRENCY_SEK)
      case Currency.USD:
        return Locale.translate(Translation.TC_CURRENCY_USD)
      case Currency.NZD:
        return Locale.translate(Translation.TC_CURRENCY_NZD)
      case Currency.CHF:
        return Locale.translate(Translation.TC_CURRENCY_CHF)
      case Currency.NOK:
        return Locale.translate(Translation.TC_CURRENCY_NOK)
      case Currency.HUF:
        return Locale.translate(Translation.TC_CURRENCY_HUF)
      case Currency.PLN:
        return Locale.translate(Translation.TC_CURRENCY_PLN)
      case Currency.ZAR:
        return Locale.translate(Translation.TC_CURRENCY_ZAR)
      case Currency.SGD:
        return Locale.translate(Translation.TC_CURRENCY_SGD)
      case Currency.ARS:
        return Locale.translate(Translation.TC_CURRENCY_ARS)
      case Currency.BRL:
        return Locale.translate(Translation.TC_CURRENCY_BRL)
      case Currency.CLP:
        return Locale.translate(Translation.TC_CURRENCY_CLP)
      case Currency.COP:
        return Locale.translate(Translation.TC_CURRENCY_COP)
      case Currency.MXN:
        return Locale.translate(Translation.TC_CURRENCY_MXN)
      case Currency.VEF:
        return Locale.translate(Translation.TC_CURRENCY_VEF)
      case Currency.CZK:
        return Locale.translate(Translation.TC_CURRENCY_CZK)
      case Currency.AED:
        return Locale.translate(Translation.TC_CURRENCY_AED)
      case Currency.TRY:
        return Locale.translate(Translation.TC_CURRENCY_TRY)
      case Currency.KRW:
        return Locale.translate(Translation.TC_CURRENCY_KRW)
      case Currency.ILS:
        return Locale.translate(Translation.TC_CURRENCY_ILS)
      case Currency.BGN:
        return Locale.translate(Translation.TC_CURRENCY_BGN)
      case Currency.ISK:
        return Locale.translate(Translation.TC_CURRENCY_ISK)
      case Currency.RON:
        return Locale.translate(Translation.TC_CURRENCY_RON)
      case Currency.QAR:
        return Locale.translate(Translation.TC_CURRENCY_QAR)
      case Currency.HKD:
        return Locale.translate(Translation.TC_CURRENCY_HKD)
      case Currency.KWD:
        return Locale.translate(Translation.TC_CURRENCY_KWD)
      case Currency.BHD:
        return Locale.translate(Translation.TC_CURRENCY_BHD)
      case Currency.CNH:
        return Locale.translate(Translation.TC_CURRENCY_CNH)
      case Currency.CNY:
        return Locale.translate(Translation.TC_CURRENCY_CNY)
      case Currency.EGP:
        return Locale.translate(Translation.TC_CURRENCY_EGP)
      case Currency.IDR:
        return Locale.translate(Translation.TC_CURRENCY_IDR)
      case Currency.INR:
        return Locale.translate(Translation.TC_CURRENCY_INR)
      case Currency.JOD:
        return Locale.translate(Translation.TC_CURRENCY_JOD)
      case Currency.KZT:
        return Locale.translate(Translation.TC_CURRENCY_KZT)
      case Currency.MAD:
        return Locale.translate(Translation.TC_CURRENCY_MAD)
      case Currency.MYR:
        return Locale.translate(Translation.TC_CURRENCY_MYR)
      case Currency.PEN:
        return Locale.translate(Translation.TC_CURRENCY_PEN)
      case Currency.PHP:
        return Locale.translate(Translation.TC_CURRENCY_PHP)
      case Currency.PYG:
        return Locale.translate(Translation.TC_CURRENCY_PYG)
      case Currency.RUB:
        return Locale.translate(Translation.TC_CURRENCY_RUB)
      case Currency.SAR:
        return Locale.translate(Translation.TC_CURRENCY_SAR)
      case Currency.SDG:
        return Locale.translate(Translation.TC_CURRENCY_SDG)
      case Currency.THB:
        return Locale.translate(Translation.TC_CURRENCY_THB)
      case Currency.TND:
        return Locale.translate(Translation.TC_CURRENCY_TND)
      case Currency.TWD:
        return Locale.translate(Translation.TC_CURRENCY_TWD)
      case Currency.UAH:
        return Locale.translate(Translation.TC_CURRENCY_UAH)
      case Currency.UYU:
        return Locale.translate(Translation.TC_CURRENCY_UYU)
      case Currency.VND:
        return Locale.translate(Translation.TC_CURRENCY_VND)
      default:
        throw new Error(`Unhandled Currency ${[Currency]}`)
    }
  }
}

export const DefaultMarketHelper: DataHelper<DefaultMarket> = {
  getId: (defaultMarket: DefaultMarket) => defaultMarket,
  getName: (defaultMarket: DefaultMarket) => {
    switch (defaultMarket) {
      case DefaultMarket.EU:
        return Locale.translate(Translation.TC_EU)

      case DefaultMarket.US:
        return Locale.translate(Translation.TC_US)

      case DefaultMarket.CUSTOM:
        return Locale.translate(Translation.TC_CUSTOM)

      default:
        throw new Error(`Unhandled DefaultMarket ${[defaultMarket]}`)
    }
  }
}

export const DateFormatHelper: DataHelper<DateFormat> = {
  getId: (dateFormat: DateFormat) => dateFormat,
  getName: (dateFormat: DateFormat) => {
    switch (dateFormat) {
      case DateFormat.EU:
        return Locale.translate(Translation.SC_EU_DATE)

      case DateFormat.US:
        return Locale.translate(Translation.SC_US_DATE)

      case DateFormat.DD_MMM_YYYY:
        return Locale.translate(Translation.SC_DD_MMM_YYYY)

      default:
        throw new Error(`Unhandled DateFormat ${[dateFormat]}`)
    }
  }
}

export const DefaultCollateralIdentifierTypeHelper: DataHelper<DefaultCollateralIdentifierType> = {
  getId: (defaultCollateralIdentifierType: DefaultCollateralIdentifierType) => defaultCollateralIdentifierType,
  getName: (defaultCollateralIdentifierType: DefaultCollateralIdentifierType) => {
    switch (defaultCollateralIdentifierType) {
      case DefaultCollateralIdentifierType.ISIN:
        return Locale.translate(Translation.TC_ISIN)

      case DefaultCollateralIdentifierType.CUSIP:
        return Locale.translate(Translation.TC_CUSIP)

      case DefaultCollateralIdentifierType.BOTH:
        return Locale.translate(Translation.TC_BOTH)

      default:
        throw new Error(`Unhandled DefaultCollateralIdentifierType ${[defaultCollateralIdentifierType]}`)
    }
  }
}

export const DateTermHelper: DataHelper<DateTerm> = {
  getId: (dateTerm: DateTerm) => dateTerm,
  getName: (dateTerm: DateTerm) => {
    switch (dateTerm) {
      case DateTerm.O:
        return 'O'
      case DateTerm.T:
        return 'T'
      case DateTerm.S:
        return 'S'
      case DateTerm.C:
        return 'C'
      case DateTerm.T_PLUS_0:
        return 'T + 0'
      case DateTerm.T_PLUS_1:
        return 'T + 1'
      case DateTerm.T_PLUS_2:
        return 'T + 2'
      case DateTerm.T_PLUS_3:
        return 'T + 3'
      default:
        throw new Error(`Unhandled Date Term ${[dateTerm]}`)
    }
  }
}

export const DealHelper: DataHelper<Deal> = {
  getId: (deal: Deal) => deal.dealId,
  getName: (deal: Deal) => deal.dealId
}

export const DatabaseReportGatewayRequestHelper: DataHelper<DatabaseReportGatewayRequest> = {
  getId: (_: DatabaseReportGatewayRequest) => '',
  getName: (_: DatabaseReportGatewayRequest) => ''
}

export const DealUpdateHelper: DataHelper<DealUpdate> = {
  getId: (deal: DealUpdate) => deal.entityId,
  getName: (deal: DealUpdate) => deal.entityId
}

export const DealExportHelper: DataHelper<DealExportEntry> = {
  getId: (dealExportEntry: DealExportEntry) => dealExportEntry.dealExportData['Trade ID'],
  getName: (dealExportEntry: DealExportEntry) => dealExportEntry.dealExportData['Trade ID']
}

export const DevSignalHelper: DataHelper<DevSignal> = {
  getId: (devSignal: DevSignal) => devSignal.correlationId,
  getName: (devSignal: DevSignal) => devSignal.correlationId
}

export const DecisionMakerHelper: DataHelper<User> = {
  getId: (user: User) => user.id,
  getName: (user: User) => `${user.firstName} ${user.lastName} ${user.shortId ? `(${user.shortId})` : ''}`
}

export const DirectionFormatHelper: DataHelper<DirectionFormat> = {
  getId: (directionFormat: DirectionFormat) => directionFormat,
  getName: (directionFormat: DirectionFormat) => {
    switch (directionFormat) {
      case DirectionFormat.BUY_SELL:
        return Locale.translate(Translation.SC_BUY_SELL)

      case DirectionFormat.RVP_RP:
        return Locale.translate(Translation.SC_RVP_RP)

      case DirectionFormat.BID_OFFER:
        return Locale.translate(Translation.SC_BID_OFFER)

      case DirectionFormat.BORROW_LEND:
        return Locale.translate(Translation.SC_BORROW_LEND)

      default:
        throw new Error(`Unhandled DirectionFormat ${[directionFormat]}`)
    }
  }
}

export const CsdHelper: DataHelper<SpreadsheetCsd> = {
  getId: (csd: SpreadsheetCsd) => csd,
  getName: (csd: SpreadsheetCsd) => csd
}

export const DirectionHelper: DataHelper<Direction | SummaryRowDirection> = {
  getId: (direction: Direction | SummaryRowDirection) => direction,
  getName: (direction: Direction | SummaryRowDirection) => {
    switch (direction) {
      case Direction.BUY:
        return Locale.translate(Translation.TC_BUY)
      case Direction.SELL:
        return Locale.translate(Translation.TC_SELL)
      case SummaryRowDirection.MULTIPLE:
        return Locale.translate(Translation.TC_MULTI)
      default:
        throw new Error(`Unhandled Direction ${[direction]}`)
    }
  }
}

export const makeDirectionHelper = (directionFormat: DirectionFormat): DataHelper<Direction | SummaryRowDirection> => {
  return {
    getId: (direction: Direction | SummaryRowDirection) => direction,
    getName: (direction: Direction | SummaryRowDirection) => {
      switch (direction) {
        case Direction.BUY:
          switch (directionFormat) {
            case DirectionFormat.RVP_RP:
              return Locale.translate(Translation.TC_REV_REPO)
            case DirectionFormat.BORROW_LEND:
              return Locale.translate(Translation.TC_BORROW)
            case DirectionFormat.BID_OFFER:
              return Locale.translate(Translation.TC_BID)
            default:
              return Locale.translate(Translation.TC_BUY)
          }
        case Direction.SELL:
          switch (directionFormat) {
            case DirectionFormat.RVP_RP:
              return Locale.translate(Translation.TC_REPO)
            case DirectionFormat.BORROW_LEND:
              return Locale.translate(Translation.TC_LEND)
            case DirectionFormat.BID_OFFER:
              return Locale.translate(Translation.TC_OFFER)
            default:
              return Locale.translate(Translation.TC_SELL)
          }
        case SummaryRowDirection.MULTIPLE:
          return Locale.translate(Translation.TC_MULTI)
        default:
          throw new Error(`Unhandled Direction ${[direction]}`)
      }
    }
  }
}

export const makeReversedDirectionHelper = (directionFormat: DirectionFormat): DataHelper<Direction | SummaryRowDirection> => {
  return {
    getId: (direction: Direction | SummaryRowDirection) => direction,
    getName: (direction: Direction | SummaryRowDirection) => {
      switch (direction) {
        case Direction.SELL:
          switch (directionFormat) {
            case DirectionFormat.RVP_RP:
              return Locale.translate(Translation.TC_REV_REPO)
            case DirectionFormat.BORROW_LEND:
              return Locale.translate(Translation.TC_BORROW)
            case DirectionFormat.BID_OFFER:
              return Locale.translate(Translation.TC_BID)
            default:
              return Locale.translate(Translation.TC_BUY)
          }
        case Direction.BUY:
          switch (directionFormat) {
            case DirectionFormat.RVP_RP:
              return Locale.translate(Translation.TC_REPO)
            case DirectionFormat.BORROW_LEND:
              return Locale.translate(Translation.TC_LEND)
            case DirectionFormat.BID_OFFER:
              return Locale.translate(Translation.TC_OFFER)
            default:
              return Locale.translate(Translation.TC_SELL)
          }
        case SummaryRowDirection.MULTIPLE:
          return Locale.translate(Translation.TC_MULTI)
        default:
          throw new Error(`Unhandled Direction ${[direction]}`)
      }
    }
  }
}

export const DurationTermHelper: DataHelper<DurationTerm> = {
  getId: (durationTerm: DurationTerm) => durationTerm,
  getName: (durationTerm: DurationTerm) => {
    switch (durationTerm) {
      case DurationTerm.N:
        return 'N'
      case DurationTerm.W:
        return 'W'
      case DurationTerm.W_2:
        return '2W'
      case DurationTerm.W_3:
        return '3W'
      case DurationTerm.M:
        return 'M'
      case DurationTerm.M_2:
        return '2M'
      case DurationTerm.M_3:
        return '3M'
      case DurationTerm.M_4:
        return '4M'
      case DurationTerm.M_5:
        return '5M'
      case DurationTerm.M_6:
        return '6M'
      case DurationTerm.M_7:
        return '7M'
      case DurationTerm.M_8:
        return '8M'
      case DurationTerm.M_9:
        return '9M'
      case DurationTerm.M_10:
        return '10M'
      case DurationTerm.M_11:
        return '11M'
      case DurationTerm.Y:
        return 'Y'
      case DurationTerm.Y_2:
        return '2Y'
      case DurationTerm.BD:
        return 'BD'
      case DurationTerm.BD_1:
        return '1BD'
      case DurationTerm.BD_2:
        return '2BD'
      case DurationTerm.BD_3:
        return '3BD'
      case DurationTerm.BD_4:
        return '4BD'
      case DurationTerm.BD_5:
        return '5BD'
      case DurationTerm.BD_6:
        return '6BD'
      case DurationTerm.O:
        return 'O'
      default:
        throw new Error(`Unhandled Duration Term ${[durationTerm]}`)
    }
  }
}

export const EntityStatusHelper: DataHelper<EntityStatus> = {
  getId: (entityStatus: EntityStatus) => entityStatus,
  getName: (entityStatus: EntityStatus) => {
    switch (entityStatus) {
      case EntityStatus.ENABLED:
        return Locale.translate(Translation.TC_ACTIVE)
      case EntityStatus.DISABLED:
        return Locale.translate(Translation.TC_SUSPENDED)
      default:
        throw new Error(`Unhandled EntityStatus ${[entityStatus]}`)
    }
  }
}

export const CallableTypeHelper: DataHelper<CallableType> = {
  getId: (callableType: CallableType) => callableType,
  getName: (callableType: CallableType) => {
    switch (callableType) {
      case CallableType.BILATERAL:
        return Locale.translate(Translation.TC_CALLABLE_TYPE_BILATERAL)
      case CallableType.UNILATERAL:
        return Locale.translate(Translation.TC_CALLABLE_TYPE_UNILATERAL)
      case CallableType.NONE:
        return Locale.translate(Translation.TC_FALSE)
      default:
        throw new Error(`Unhandled callable type: ${[callableType]}`)
    }
  }
}

export const EoniaRateHelper: DataHelper<EoniaRate> = {
  getId: (eoniaRate: EoniaRate) => eoniaRate.id,
  getName: (eoniaRate: EoniaRate) => eoniaRate.id
}

export const EstrRateHelper: DataHelper<EstrRate> = {
  getId: (estrRate: EstrRate) => estrRate.id,
  getName: (estrRate: EstrRate) => estrRate.id
}

export const ExDividendDayConventionHelper: DataHelper<ExDividendDayConvention> = {
  getId: (exDividendDayConvention: ExDividendDayConvention) => exDividendDayConvention,
  getName: (exDividendDayConvention: ExDividendDayConvention) => {
    switch (exDividendDayConvention) {
      case ExDividendDayConvention.BUSINESS_DAYS:
        return Locale.translate(Translation.TC_BUSINESS_DAYS)
      case ExDividendDayConvention.CALENDAR_DAYS:
        return Locale.translate(Translation.TC_CALENDAR_DAYS)
      default:
        throw new Error(`Unhandled ExDividendDayConvention ${[exDividendDayConvention]}`)
    }
  }
}

export const FatFingerProductSectorLimitHelper: DataHelper<FatFingerProductSectorLimit> = {
  getId: (fatFingerProductSectorLimit: FatFingerProductSectorLimit) => fatFingerProductSectorLimit.productSectorId,
  getName: (fatFingerProductSectorLimit: FatFingerProductSectorLimit) => fatFingerProductSectorLimit.productSectorId
}

export const FatFingerUserLimitHelper: DataHelper<FatFingerUserLimit> = {
  getId: (fatFingerUserLimit: FatFingerUserLimit) => fatFingerUserLimit.userId,
  getName: (fatFingerUserLimit: FatFingerUserLimit) => fatFingerUserLimit.userId
}

export const FxRateHelper: DataHelper<FxRate> = {
  getId: (fxRate: FxRate) => fxRate.id,
  getName: (fxRate: FxRate) => `${fxRate.baseCurrency}/${fxRate.quoteCurrency}`
}

export const HaircutBandHelper: DataHelper<HaircutBand> = {
  getId: (haircutBand: HaircutBand) => haircutBand.id,
  getName: (haircutBand: HaircutBand) => {
    const { minDays, maxDays } = haircutBand
    if (maxDays === null) {
      return Locale.translate(Translation.TC_HAIRCUT_BAND_GREATER_THAN, { days: minDays - 1 })
    }
    if (minDays === maxDays) {
      return Locale.translate(Translation.TC_HAIRCUT_BAND_UNIQUE, { days: minDays })
    }
    return Locale.translate(Translation.TC_HAIRCUT_BAND_RANGE, { minDays, maxDays })
  }
}

export const HaircutCollateralBandHelper: DataHelper<HaircutCollateralBand> = {
  getId: (haircutCollateralBand: HaircutCollateralBand) => haircutCollateralBand.id,
  getName: (haircutCollateralBand: HaircutCollateralBand) => {
    const { minYears, maxYears } = haircutCollateralBand
    if (maxYears === null) {
      return Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_GREATER_THAN, { years: minYears })
    }
    if (minYears === null) {
      return Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_LESS_THAN, { years: maxYears })
    }
    return Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_RANGE, { minYears, maxYears })
  }
}

export const HaircutEntryHelper: DataHelper<HaircutEntry> = {
  getId: (haircutEntry: HaircutEntry) => haircutEntry.id,
  getName: (haircutEntry: HaircutEntry) => haircutEntry.id
}

export const HaircutTypeHelper: DataHelper<HaircutType> = {
  getId: (haircutType: HaircutType) => haircutType,
  getName: (haircutType: HaircutType) => {
    switch (haircutType) {
      case HaircutType.HAIRCUT:
        return Locale.translate(Translation.TC_HAIRCUT_TYPE_HAIRCUT)

      case HaircutType.COLLATERAL:
        return Locale.translate(Translation.TC_HAIRCUT_TYPE_COLLATERAL)

      default:
        throw new Error(`Unhandled Haircut Type ${[haircutType]}`)
    }
  }
}

export const BrowserNotificationsHelper: DataHelper<NotificationPermission> = {
  getId: (notificationPermission: NotificationPermission) => notificationPermission,
  getName: (notificationPermission: NotificationPermissionEnum) => {
    switch (notificationPermission) {
      case NotificationPermissionEnum.DEFAULT:
      case NotificationPermissionEnum.DENIED:
        return Locale.translate(Translation.TC_FALSE)
      case NotificationPermissionEnum.GRANTED:
        return Locale.translate(Translation.TC_TRUE)
      default:
        return assertNever(notificationPermission, `Unhandled Browser Notification Permission ${notificationPermission}`)
    }
  }
}

export const HaircutSignTypeHelper: DataHelper<HaircutSign> = {
  getId: (haircutSignType: HaircutSign) => haircutSignType,
  getName: (haircutSignType: HaircutSign) => {
    switch (haircutSignType) {
      case HaircutSign.ACTUAL_SELL_SIDE:
        return Locale.translate(Translation.TC_ACTUAL_SELL_SIDE)

      case HaircutSign.DIRECTIONAL:
        return Locale.translate(Translation.TC_DIRECTIONAL)

      case HaircutSign.ABSOLUTE:
        return Locale.translate(Translation.TC_ABSOLUTE)

      case HaircutSign.NEGATIVE_REV_REPO:
        return Locale.translate(Translation.TC_NEGATIVE_REV_REPO)

      default:
        throw new Error(`Unhandled HaircutSign ${[haircutSignType]}`)
    }
  }
}

export const CopyPasteFormatHelper: DataHelper<CopyPasteFormat> = {
  getId: (copyPasteFormat: CopyPasteFormat) => copyPasteFormat,
  getName: (copyPasteFormat: CopyPasteFormat) => {
    switch (copyPasteFormat) {
      case CopyPasteFormat.TABULAR:
        return Locale.translate(Translation.TC_TABULAR)

      case CopyPasteFormat.PLAIN_TEXT:
        return Locale.translate(Translation.TC_PLAIN_TEXT)

      default:
        throw new Error(`Unhandled CopyPasteFormat ${copyPasteFormat}`)
    }
  }
}

export const HolidayHelper: DataHelper<Holiday> = {
  getId: (holiday: Holiday) => holiday.currency,
  getName: (holiday: Holiday) => holiday.currency
}

export const IndexHelper: DataHelper<Index> = {
  getId: (index: Index) => index.id,
  getName: (index: Index) => index.code
}

export const IndexLagHelper: DataHelper<IndexLag> = {
  getId: (indexLag: IndexLag) => indexLag,
  getName: (indexLag: IndexLag) => {
    switch (indexLag) {
      case IndexLag.THREE_MONTHS:
        return Locale.translate(Translation.TC_INDEX_LAG_THREE_MONTHS)

      case IndexLag.EIGHT_MONTHS:
        return Locale.translate(Translation.TC_INDEX_LAG_EIGHT_MONTHS)

      default:
        throw new Error(`Unhandled Index Lag ${[indexLag]}`)
    }
  }
}

export const InFlightOrganisationHelper: DataHelper<InFlightOrganisation> = {
  getId: (inFlightOrganisation: InFlightOrganisation) => inFlightOrganisation.organisationId,
  getName: (inFlightOrganisation: InFlightOrganisation) => inFlightOrganisation.organisationId
}

export const InFlightTargetOrganisationHelper: DataHelper<InFlightTargetOrganisation> = {
  getId: (inFlightTargetOrganisation: InFlightTargetOrganisation) => inFlightTargetOrganisation.organisationId,
  getName: (inFlightTargetOrganisation: InFlightTargetOrganisation) => inFlightTargetOrganisation.organisationId
}

export const InstrumentHelper: DataHelper<Instrument> = {
  getId: (instrument: Instrument) => (isCollateral(instrument) ? instrument.id : instrument.id),
  getName: (instrument: Instrument) => instrument.name
}

export const MarketHelper: DataHelper<Market> = {
  getId: (market: Market) => market,
  getName: (market: Market) => {
    switch (market) {
      case Market.EUROPE:
        return Locale.translate(Translation.TC_MARKET_EUROPE)
      case Market.US:
        return Locale.translate(Translation.TC_MARKET_US)
      default:
        throw new Error(`Unhandled Market ${[market]}`)
    }
  }
}

export const RelationshipActionHelper: DataHelper<RelationshipAction> = {
  getId: (relationshipAction: RelationshipAction) => relationshipAction,
  getName: (relationshipAction: RelationshipAction) => relationshipAction
}

export const MasterLegalAgreementTypeHelper: DataHelper<MasterLegalAgreementType> = {
  getId: (masterLegalAgreementType: MasterLegalAgreementType) => masterLegalAgreementType,
  getName: (masterLegalAgreementType: MasterLegalAgreementType) => masterLegalAgreementType
}

export const MidTradeActionHelper: DataHelper<MidTradeAction> = {
  getId: (midTradeAction: MidTradeAction) => midTradeAction,
  getName: (midTradeAction: MidTradeAction) => {
    switch (midTradeAction) {
      case MidTradeAction.CLOSE:
        return Locale.translate(Translation.TC_TERMINATE)
      case MidTradeAction.RESIZE:
      case MidTradeAction.RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE:
        return Locale.translate(Translation.TC_CLOSE_OPEN)
      case MidTradeAction.REPRICE:
        return Locale.translate(Translation.TC_RERATE)
      case MidTradeAction.ROLL:
        return Locale.translate(Translation.TC_MTE_ROLL)
      default:
        throw new Error(`Unhandled MidTradeAction ${[midTradeAction]}`)
    }
  }
}

export const MidTradeResponseHelper: DataHelper<MidTradeResponse> = {
  getId: (midTradeResponse: MidTradeResponse) => midTradeResponse,
  getName: (midTradeAction: MidTradeResponse) => {
    switch (midTradeAction) {
      case MidTradeResponse.ACCEPT:
        return Locale.translate(Translation.TC_ACTION_ACCEPT)
      case MidTradeResponse.REJECT:
        return Locale.translate(Translation.TC_ACTION_REJECT)
      case MidTradeResponse.SEND:
        return Locale.translate(Translation.TC_ACTION_SEND)
      default:
        throw new Error(`Unhandled MidTradeResponse ${[midTradeAction]}`)
    }
  }
}

export const NegotiationHelper: DataHelper<Negotiation> = {
  getId: (negotiation: Negotiation) => negotiation.negotiationId,
  getName: (negotiation: Negotiation) => negotiation.negotiationId
}

export const NegotiationAggregateHelper: DataHelper<NegotiationAggregate> = {
  getId: (negotiationAggregate: NegotiationAggregate) => negotiationAggregate.negotiationAggregateId,
  getName: (negotiationAggregate: NegotiationAggregate) => negotiationAggregate.negotiationAggregateId
}

export const NegotiationOverrideHelper: DataHelper<NegotiationOverride> = {
  getId: (negotiationPrice: NegotiationOverride) => negotiationPrice.negotiationId,
  getName: (negotiationPrice: NegotiationOverride) => negotiationPrice.negotiationId
}

export const NegotiationUpdateHelper: DataHelper<NegotiationUpdate> = {
  getId: (negotiation: NegotiationUpdate) => negotiation.entityId,
  getName: (negotiation: NegotiationUpdate) => negotiation.entityId
}

export const NegotiationHistoryHelper: DataHelper<NegotiationHistory> = {
  getId: (negotiationHistory: NegotiationHistory) => negotiationHistory.id,
  getName: (negotiationHistory: NegotiationHistory) => negotiationHistory.id
}

export const NotesHelper: DataHelper<Notes> = {
  getId: (note: Notes) => note.id,
  getName: (note: Notes) => note.id
}

export const OrderHelper: DataHelper<Order> = {
  getId: (order: Order) => order.orderId,
  getName: (order: Order) => order.orderId
}

export const OrderRecordKeepingHelper: DataHelper<OrderRecordKeeping> = {
  getId: () => 'ORK_ID', // TODO - Tidy up this requirement to have a helper for every entity type
  getName: () => 'ORK_NAME'
}

export const getOrderTermByString = (term: string): OrderTerm => {
  switch (term) {
    case 'O-N':
      return OrderTerm.O_N
    case 'O-W':
      return OrderTerm.O_W
    case 'O-2W':
      return OrderTerm.O_2W
    case 'O-3W':
      return OrderTerm.O_3W
    case 'O-M':
      return OrderTerm.O_M
    case 'O-2M':
      return OrderTerm.O_2M
    case 'O-3M':
      return OrderTerm.O_3M
    case 'O-4M':
      return OrderTerm.O_4M
    case 'O-5M':
      return OrderTerm.O_5M
    case 'O-6M':
      return OrderTerm.O_6M
    case 'O-7M':
      return OrderTerm.O_7M
    case 'O-8M':
      return OrderTerm.O_8M
    case 'O-9M':
      return OrderTerm.O_9M
    case 'O-10M':
      return OrderTerm.O_10M
    case 'O-11M':
      return OrderTerm.O_11M
    case 'T-N':
      return OrderTerm.T_N
    case 'T-W':
      return OrderTerm.T_W
    case 'T-2W':
      return OrderTerm.T_2W
    case 'T-3W':
      return OrderTerm.T_3W
    case 'T-M':
      return OrderTerm.T_M
    case 'T-2M':
      return OrderTerm.T_2M
    case 'T-3M':
      return OrderTerm.T_3M
    case 'T-4M':
      return OrderTerm.T_4M
    case 'T-5M':
      return OrderTerm.T_5M
    case 'T-6M':
      return OrderTerm.T_6M
    case 'T-7M':
      return OrderTerm.T_7M
    case 'T-8M':
      return OrderTerm.T_8M
    case 'T-9M':
      return OrderTerm.T_9M
    case 'T-10M':
      return OrderTerm.T_10M
    case 'T-11M':
      return OrderTerm.T_11M
    case 'S-N':
      return OrderTerm.S_N
    case 'S-W':
      return OrderTerm.S_W
    case 'S-2W':
      return OrderTerm.S_2W
    case 'S-3W':
      return OrderTerm.S_3W
    case 'S-M':
      return OrderTerm.S_M
    case 'S-2M':
      return OrderTerm.S_2M
    case 'S-3M':
      return OrderTerm.S_3M
    case 'S-4M':
      return OrderTerm.S_4M
    case 'S-5M':
      return OrderTerm.S_5M
    case 'S-6M':
      return OrderTerm.S_6M
    case 'S-7M':
      return OrderTerm.S_7M
    case 'S-8M':
      return OrderTerm.S_8M
    case 'S-9M':
      return OrderTerm.S_9M
    case 'S-10M':
      return OrderTerm.S_10M
    case 'S-11M':
      return OrderTerm.S_11M
    case 'S-Y':
      return OrderTerm.S_Y
    case 'C-N':
      return OrderTerm.C_N
    case 'C-W':
      return OrderTerm.C_W
    case 'C-2W':
      return OrderTerm.C_2W
    case 'C-3W':
      return OrderTerm.C_3W
    case 'C-M':
      return OrderTerm.C_M
    case 'C-2M':
      return OrderTerm.C_2M
    case 'C-3M':
      return OrderTerm.C_3M
    case 'C-4M':
      return OrderTerm.C_4M
    case 'C-5M':
      return OrderTerm.C_5M
    case 'C-6M':
      return OrderTerm.C_6M
    case 'C-7M':
      return OrderTerm.C_7M
    case 'C-8M':
      return OrderTerm.C_8M
    case 'C-9M':
      return OrderTerm.C_9M
    case 'C-10M':
      return OrderTerm.C_10M
    case 'C-11M':
      return OrderTerm.C_11M
    case 'C-Y':
      return OrderTerm.C_Y
    case 'Custom':
      return OrderTerm.CUSTOM
    case 'O-O':
      return OrderTerm.O_O
    case 'T-O':
      return OrderTerm.T_O
    case 'S-O':
      return OrderTerm.S_O
    case 'C-O':
      return OrderTerm.C_O
    case 'Custom-O':
      return OrderTerm.CUSTOM_O
    default:
      throw new Error(`Unhandled Term ${[term]}`)
  }
}
export const OrderTermHelper: DataHelper<OrderTerm> = {
  getId: (term: OrderTerm) => term,

  getName: (term: OrderTerm) => {
    switch (term) {
      case OrderTerm.O_N:
        return 'O-N'
      case OrderTerm.O_W:
        return 'O-W'
      case OrderTerm.O_2W:
        return 'O-2W'
      case OrderTerm.O_3W:
        return 'O-3W'
      case OrderTerm.O_M:
        return 'O-M'
      case OrderTerm.O_2M:
        return 'O-2M'
      case OrderTerm.O_3M:
        return 'O-3M'
      case OrderTerm.O_4M:
        return 'O-4M'
      case OrderTerm.O_5M:
        return 'O-5M'
      case OrderTerm.O_6M:
        return 'O-6M'
      case OrderTerm.O_7M:
        return 'O-7M'
      case OrderTerm.O_8M:
        return 'O-8M'
      case OrderTerm.O_9M:
        return 'O-9M'
      case OrderTerm.O_10M:
        return 'O-10M'
      case OrderTerm.O_11M:
        return 'O-11M'
      case OrderTerm.T_N:
        return 'T-N'
      case OrderTerm.T_W:
        return 'T-W'
      case OrderTerm.T_2W:
        return 'T-2W'
      case OrderTerm.T_3W:
        return 'T-3W'
      case OrderTerm.T_M:
        return 'T-M'
      case OrderTerm.T_2M:
        return 'T-2M'
      case OrderTerm.T_3M:
        return 'T-3M'
      case OrderTerm.T_4M:
        return 'T-4M'
      case OrderTerm.T_5M:
        return 'T-5M'
      case OrderTerm.T_6M:
        return 'T-6M'
      case OrderTerm.T_7M:
        return 'T-7M'
      case OrderTerm.T_8M:
        return 'T-8M'
      case OrderTerm.T_9M:
        return 'T-9M'
      case OrderTerm.T_10M:
        return 'T-10M'
      case OrderTerm.T_11M:
        return 'T-11M'
      case OrderTerm.S_N:
        return 'S-N'
      case OrderTerm.S_W:
        return 'S-W'
      case OrderTerm.S_2W:
        return 'S-2W'
      case OrderTerm.S_3W:
        return 'S-3W'
      case OrderTerm.S_M:
        return 'S-M'
      case OrderTerm.S_2M:
        return 'S-2M'
      case OrderTerm.S_3M:
        return 'S-3M'
      case OrderTerm.S_4M:
        return 'S-4M'
      case OrderTerm.S_5M:
        return 'S-5M'
      case OrderTerm.S_6M:
        return 'S-6M'
      case OrderTerm.S_7M:
        return 'S-7M'
      case OrderTerm.S_8M:
        return 'S-8M'
      case OrderTerm.S_9M:
        return 'S-9M'
      case OrderTerm.S_10M:
        return 'S-10M'
      case OrderTerm.S_11M:
        return 'S-11M'
      case OrderTerm.S_Y:
        return 'S-Y'
      case OrderTerm.C_N:
        return 'C-N'
      case OrderTerm.C_W:
        return 'C-W'
      case OrderTerm.C_2W:
        return 'C-2W'
      case OrderTerm.C_3W:
        return 'C-3W'
      case OrderTerm.C_M:
        return 'C-M'
      case OrderTerm.C_2M:
        return 'C-2M'
      case OrderTerm.C_3M:
        return 'C-3M'
      case OrderTerm.C_4M:
        return 'C-4M'
      case OrderTerm.C_5M:
        return 'C-5M'
      case OrderTerm.C_6M:
        return 'C-6M'
      case OrderTerm.C_7M:
        return 'C-7M'
      case OrderTerm.C_8M:
        return 'C-8M'
      case OrderTerm.C_9M:
        return 'C-9M'
      case OrderTerm.C_10M:
        return 'C-10M'
      case OrderTerm.C_11M:
        return 'C-11M'
      case OrderTerm.C_Y:
        return 'C-Y'
      case OrderTerm.CUSTOM:
        return 'Custom'
      case OrderTerm.O_O:
        return 'O-O'
      case OrderTerm.T_O:
        return 'T-O'
      case OrderTerm.S_O:
        return 'S-O'
      case OrderTerm.C_O:
        return 'C-O'
      case OrderTerm.CUSTOM_O:
        return 'Custom-O'
      default:
        throw new Error(`Unhandled Term ${[term]}`)
    }
  }
}

export const EvergreenNoticeHelper: DataHelper<EvergreenNoticePeriod> = {
  getId: (evergreen: EvergreenNoticePeriod) => evergreen,
  getName: (evergreen: EvergreenNoticePeriod) => {
    switch (evergreen) {
      case EvergreenNoticePeriod.THIRTY:
        return '30'
      case EvergreenNoticePeriod.SIXTY:
        return '60'
      case EvergreenNoticePeriod.NINETY:
        return '90'
      default:
        throw new Error(`Unhandled Evergreen Notice Period ${[evergreen]}`)
    }
  }
}

export const MarketTypeHelper: DataHelper<MarketType> = {
  getId: (marketType: MarketType) => marketType,
  getName: (marketType: MarketType) => {
    switch (marketType) {
      case MarketType.PRIMARY:
        return Locale.translate(Translation.TC_PRIMARY)
      case MarketType.SECONDARY:
        return Locale.translate(Translation.TC_SECONDARY)
      default:
        return assertNever(marketType, `Unhandled Market Type ${[marketType]}`)
    }
  }
}

export const OrganisationHelper: DataHelper<Organisation> = {
  getId: (organisation: Organisation) => organisation.id,
  getName: (organisation: Organisation) => organisation.legalName
}

export const IssuerOrganisationHelper: DataHelper<IssuerOrganisation> = {
  getId: (issuerOrganisation: IssuerOrganisation) => issuerOrganisation.id,
  getName: (issuerOrganisation: IssuerOrganisation) => issuerOrganisation.issuerName || ''
}

export const OrganisationMetaHelper: DataHelper<OrganisationMeta> = {
  getId: (organisationMeta: OrganisationMeta) => organisationMeta.id,
  getName: (organisationMeta: OrganisationMeta) => organisationMeta.id
}

export const OrganisationTypeHelper: DataHelper<OrganisationType> = {
  getId: (organisationType: OrganisationType) => organisationType,
  getName: (organisationType: OrganisationType) => {
    switch (organisationType) {
      case OrganisationType.BUY_SIDE:
        return Locale.translate(Translation.SC_BUY_SIDE)
      case OrganisationType.SELL_SIDE:
        return Locale.translate(Translation.SC_SELL_SIDE)
      default:
        throw new Error(`Unhandled Organisation Type ${[organisationType]}`)
    }
  }
}

export const PlatformHelper: DataHelper<Platform> = {
  getId: (sector: Platform) => sector.id,
  getName: (sector: Platform) => sector.id
}

export const PermissionReportRequestHelper: DataHelper<PermissionReportRequest> = {
  getId: (permissionReportRequest: PermissionReportRequest) => permissionReportRequest.correlationId,
  getName: (permissionReportRequest: PermissionReportRequest) => permissionReportRequest.correlationId
}

export const QueryRequestHelper: DataHelper<QueryRequest> = {
  getId: (queryRequest: QueryRequest) => queryRequest.queryGroupId!!,
  getName: (queryRequest: QueryRequest) => queryRequest.queryGroupId!!
}

export const PrimeBrokerHelper: DataHelper<PrimeBroker> = {
  getId: (primeBroker: PrimeBroker) => primeBroker.id,
  getName: (primeBroker: PrimeBroker) => primeBroker.name
}

export const ProductHelper: DataHelper<Product> = {
  getId: (product: Product) => product.id,
  getName: (product: Product) => product.name
}

export const ProductSectorHelper: DataHelper<ProductSector> = {
  getId: (sector: ProductSector) => sector.id,
  getName: (sector: ProductSector) => sector.name
}

export const ProductSectorMappingHelper: DataHelper<ProductSectorMappingDetail> = {
  getId: (mapping: ProductSectorMappingDetail) => mapping.id,
  getName: (mapping: ProductSectorMappingDetail) => mapping.id
}

export const ProductSectorMappingDraftHelper: DataHelper<ProductSectorMappingDraftDetail> = {
  getId: (mapping: ProductSectorMappingDraftDetail) => mapping.id,
  getName: (mapping: ProductSectorMappingDraftDetail) => mapping.id
}

export const ProductSectorMappingStateHelper: DataHelper<ProductSectorMappingState> = {
  getId: (state: ProductSectorMappingState) => state.id,
  getName: (state: ProductSectorMappingState) => state.id
}

export const ProductSectorAggregateHelper: DataHelper<ProductSectorAggregate> = {
  getId: (agg: ProductSectorAggregate) => (agg.productSector ? agg.productSector.id : ''),
  getName: (agg: ProductSectorAggregate) => (agg.product && agg.productSector ? `${agg.product.name} - ${agg.productSector.name}` : '')
}

export const RateTypeHelper: DataHelper<RateType> = {
  getId: (rateType: RateType) => rateType,
  getName: (rateType: RateType) => {
    switch (rateType) {
      case RateType.FIXED:
        return Locale.translate(Translation.TC_FIXED)

      case RateType.FLOATING:
        return Locale.translate(Translation.TC_FLOATING)

      default:
        throw new Error(`Unhandled Rate Type ${[rateType]}`)
    }
  }
}

export const ReferenceDataManagementDataHelper: DataHelper<ReferenceDataManagementData> = {
  getId: (referenceDataManagementData: ReferenceDataManagementData) => referenceDataManagementData.workflowId,
  getName: (referenceDataManagementData: ReferenceDataManagementData) => referenceDataManagementData.workflowId
}

export const SotwPositionHelper: DataHelper<SotwPosition> = {
  getId: (_: SotwPosition) => '',
  getName: (_: SotwPosition) => ''
}

export const SotwResetHelper: DataHelper<SotwReset> = {
  getId: (_: SotwReset) => '',
  getName: (_: SotwReset) => ''
}

export const ReferenceRateHelper: DataHelper<ReferenceRate> = {
  getId: (rate: ReferenceRate) => rate.id,
  getName: (rate: ReferenceRate) => rate.name
}

export const ExportFailedRetryDataHelper: DataHelper<ExportFailedRetryData> = {
  getId: (retryData: ExportFailedRetryData) => retryData.id,
  getName: (retryData: ExportFailedRetryData) => retryData.id
}

export const RepoClassificationHelper: DataHelper<RepoClassification> = {
  getId: (repoClassification: RepoClassification) => repoClassification,
  getName: (repoClassification: RepoClassification) => {
    switch (repoClassification) {
      case RepoClassification.FIXED:
        return Locale.translate(Translation.TC_REPO_CLASSIFICATION_FIXED)
      case RepoClassification.OPEN:
        return Locale.translate(Translation.TC_REPO_CLASSIFICATION_OPEN)
      case RepoClassification.OPEN_EVERGREEN:
        return Locale.translate(Translation.TC_REPO_CLASSIFICATION_OPEN_EVERGREEN)
      default:
        throw new Error(`Unhandled RepoClassification ${[repoClassification]}`)
    }
  }
}

export const RepoTypeHelper: DataHelper<RepoType> = {
  getId: (repoType: RepoType) => repoType,
  getName: (repoType: RepoType | string) => {
    switch (repoType) {
      case RepoType.BSB:
        return Locale.translate(Translation.TC_REPO_TYPE_BSB)
      case RepoType.CLASSIC:
        return Locale.translate(Translation.TC_REPO_TYPE_CLASSIC)
      case RepoType.STOCK_BORROWING_LENDING:
        return Locale.translate(Translation.TC_REPO_TYPE_STOCK_BORROWING_LENDING)
      case Locale.translate(Translation.SC_MULTIPLE):
        return Locale.translate(Translation.SC_MULTIPLE)
      default:
        throw new Error(`Unhandled RepoType ${[repoType]}`)
    }
  }
}

export const RFQTypeHelper: DataHelper<RfqType> = {
  getId: (rfqType: RfqType) => rfqType,
  getName: (rfqType: RfqType) => {
    switch (rfqType) {
      case RfqType.FIRM:
        return Locale.translate(Translation.TC_RFQ_TYPE_FIRM)
      case RfqType.INDICATIVE:
        return Locale.translate(Translation.TC_RFQ_TYPE_INDICATIVE)
      default:
        throw new Error(`Unhandled RfqType ${[rfqType]}`)
    }
  }
}

export const AssetTypeHelper: DataHelper<AssetType> = {
  getId: (assetType: AssetType) => assetType,
  getName: (assetType: AssetType) => {
    switch (assetType) {
      case AssetType.MORT_AGENCY:
        return Locale.translate(Translation.TC_MORT_AGENCY_ASSET_TYPE)
      case AssetType.MORT_NON_AGENCY:
        return Locale.translate(Translation.TC_MORT_NON_AGENCY_ASSET_TYPE)
      default:
        return assetType
    }
  }
}

export const ServicingTypeHelper: DataHelper<ServicingType | typeof ServicingTypeNewDeal> = {
  getId: (servicingType: ServicingType | typeof ServicingTypeNewDeal) => servicingType,
  getName: (servicingType: ServicingType | typeof ServicingTypeNewDeal) => {
    switch (servicingType) {
      case ServicingType.ROLL:
        return Locale.translate(Translation.TC_ROLL)
      case ServicingType.CLOSE_OPEN:
        return Locale.translate(Translation.TC_CLOSE_OPEN)
      case ServicingType.RERATE:
        return Locale.translate(Translation.TC_RERATE)
      case ServicingType.RETURN_RECALL:
        return Locale.translate(Translation.TC_RETURN_RECALL)
      case ServicingType.SUBSTITUTION:
        return Locale.translate(Translation.TC_SUBSTITUTION)
      case ServicingType.TERMINATION:
        return Locale.translate(Translation.TC_TERMINATION)
      case ServicingType.ROLL_IN_PLACE:
        return Locale.translate(Translation.TC_ROLL_IN_PLACE)
      case ServicingType.SERVICING_AMEND:
      case ServicingType.SERVICING_TRADER_AMEND:
        return Locale.translate(Translation.TC_AMEND_TRADE)
      case ServicingTypeNewDeal:
        return Locale.translate(Translation.TC_NEW_DEAL)
      case ServicingType.SERVICING_TRADER_CANCEL:
        return Locale.translate(Translation.TC_CANCEL_TRADE)
      case ServicingType.SERVICING_ADMIN_CANCEL:
        return Locale.translate(Translation.TC_CANCEL_TRADE)
      default:
        return assertNever(servicingType, `Unhandled ServicingType ${[JSON.stringify(servicingType)]}`)
    }
  }
}

export const SettlementVenueHelper: DataHelper<SettlementVenue> = {
  getId: (venue: SettlementVenue) => venue,
  getName: (venue: SettlementVenue) => {
    switch (venue) {
      case SettlementVenue.Bilateral:
        return Locale.translate(Translation.SC_BILATERAL_SETTLEMENT)

      case SettlementVenue.Direct:
        return Locale.translate(Translation.SC_DIRECT_CLEARING_SETTLEMENT)

      case SettlementVenue.Sponsored:
        return Locale.translate(Translation.SC_SPONSORED_CLEARING_SETTLEMENT)

      case SettlementVenue.Triparty:
        return Locale.translate(Translation.SC_TRIPARTY_SETTLEMENT)

      default:
        return venue
    }
  }
}

export const SpreadsheetSettlementVenueHelper: DataHelper<SpreadsheetSettlementVenue> = {
  getId: (settlementType: SpreadsheetSettlementVenue) => settlementType,
  getName: (settlementType: SpreadsheetSettlementVenue) => {
    switch (settlementType) {
      case SpreadsheetSettlementVenue.Bilateral:
        return Locale.translate(Translation.SC_BILATERAL_SETTLEMENT)

      case SpreadsheetSettlementVenue.Direct:
        return Locale.translate(Translation.SC_DIRECT_CLEARING_SETTLEMENT)

      case SpreadsheetSettlementVenue.Sponsored:
        return Locale.translate(Translation.SC_SPONSORED_CLEARING_SETTLEMENT)

      default:
        return settlementType
    }
  }
}

export const SponsorHelper: DataHelper<Sponsor> = {
  getId: (sponsor: Sponsor) => sponsor.id,
  getName: (sponsor: Sponsor) => sponsor.publishedName
}

export const SponsorTypeHelper: DataHelper<SponsorType> = {
  getId: (sponsorType: SponsorType) => sponsorType,
  getName: (sponsorType: SponsorType) => sponsorType
}

export const SpreadsheetMappingHelper: DataHelper<SpreadsheetMapping> = {
  getId: (mapping: SpreadsheetMapping) => mapping.id,
  getName: (mapping: SpreadsheetMapping) => mapping.name
}

export const TradeConditionFlagHelper: DataHelper<TradeConditionFlag> = {
  getId: (condition: TradeConditionFlag) => condition,
  getName: (condition: TradeConditionFlag) => {
    switch (condition) {
      case TradeConditionFlag.NONE:
      case null:
        return Locale.translate(Translation.SC_NONE)
      case TradeConditionFlag.ROLL:
        return Locale.translate(Translation.SC_ROLL)
      case TradeConditionFlag.UNWIND:
        return Locale.translate(Translation.SC_UNWIND)
      case TradeConditionFlag.SUB:
        return Locale.translate(Translation.SC_SUB)
      default:
        return assertNever(condition, `Unhandled TradeConditionFlag ${[JSON.stringify(condition)]}`)
    }
  }
}

export const TradingControlLimitHelper: DataHelper<TradingControlLimit> = {
  getId: (entity: TradingControlLimit) => entity.id,
  getName: (entity: TradingControlLimit) => entity.id
}

export const TradingControlLimitUserUtilisationHelper: DataHelper<TradingControlLimitUserUtilisation> = {
  getId: (entity: TradingControlLimitUserUtilisation) => entity.userId,
  getName: (entity: TradingControlLimitUserUtilisation) => entity.userId
}

export const TradingEntityHelper: DataHelper<TradingEntity> = {
  getId: (entity: TradingEntity) => entity.id,
  getName: (entity: TradingEntity) => entity.publishedName
}

export const TradingEntityProductPermissionHelper: DataHelper<TradingEntityProductPermission> = {
  getId: (productPermission: TradingEntityProductPermission) => productPermission,
  getName: (productPermission: TradingEntityProductPermission) => {
    switch (productPermission) {
      case TradingEntityProductPermission.VIEW:
        return Locale.translate(Translation.TC_VIEW)
      case TradingEntityProductPermission.TRADE:
        return Locale.translate(Translation.TC_TRADE)
      default:
        throw new Error(`Unhandled trading entity product permission ${[productPermission]}`)
    }
  }
}

export const TradingEntityMetaHelper: DataHelper<TradingEntityMeta> = {
  getId: (entity: TradingEntityMeta) => entity.id,
  getName: (entity: TradingEntityMeta) => entity.id
}

export const TradingEntityTypeHelper: DataHelper<ExtendedTradingEntityType> = {
  getId: (tradingEntityType: ExtendedTradingEntityType) => tradingEntityType,
  getName: (tradingEntityType: ExtendedTradingEntityType) => {
    switch (tradingEntityType) {
      case ExtendedTradingEntityType.STANDARD:
        return Locale.translate(Translation.TC_STANDARD)
      case ExtendedTradingEntityType.BLOCK:
        return Locale.translate(Translation.TC_BLOCK)
      case ExtendedTradingEntityType.BOTH:
        return Locale.translate(Translation.TC_BOTH)
      default:
        throw new Error(`Unhandled trading entity type ${[tradingEntityType]}`)
    }
  }
}

export const TradingEntityClearingHelper: DataHelper<TradingEntityClearing> = {
  getId: (entity: TradingEntityClearing) => entity.id,
  getName: (entity: TradingEntityClearing) => entity.id
}

export const TradingEntityWithAliasHelper: DataHelper<TradingEntityWithAlias> = {
  getId: (entity: TradingEntityWithAlias) => entity.id,
  getName: (entity: TradingEntityWithAlias) => entity.alias || entity.publishedName
}

export const TradingRelationshipHelper: DataHelper<TradingRelationship> = {
  getId: (relationship: TradingRelationship) => relationship.id,
  getName: (relationship: TradingRelationship) => relationship.id
}

export const TradingTypeHelper: DataHelper<TradingType> = {
  getId: (tradingType: TradingType) => tradingType,
  getName: (tradingType: TradingType) => {
    switch (tradingType) {
      case TradingType.CASH:
        return Locale.translate(Translation.TC_BASKET_TRADING_CASH)
      case TradingType.EITHER:
        return Locale.translate(Translation.TC_BASKET_TRADING_EITHER)
      case TradingType.NOTIONAL:
        return Locale.translate(Translation.TC_BASKET_TRADING_NOTIONAL)
      default:
        throw new Error(`Unhandled Collateral Basket trading type ${tradingType}`)
    }
  }
}

export const UserHelper: DataHelper<User> = {
  getId: (user: User) => user.id,
  getName: (user: User) => `${user.firstName} ${user.lastName}`
}

export const UserPreferenceHelper: DataHelper<UserPreference> = {
  getId: (userPreference: UserPreference) => userPreference.id,
  getName: (userPreference: UserPreference) => userPreference.id
}

export const UserSettingsHelper: DataHelper<UserSettings> = {
  getId: (userSettings: UserSettings) => userSettings.id,
  getName: (userSettings: UserSettings) => userSettings.id
}

export const WorkingDaysHelper: DataHelper<WorkingDays> = {
  getId: (workingDays: WorkingDays) => workingDays.id,
  getName: (workingDays: WorkingDays) => workingDays.id
}

export const StartTimeZoneHelper: DataHelper<StartTimeZone> = {
  getId: (startTimeZone: StartTimeZone) => startTimeZone,
  getName: (startTimeZone: StartTimeZone) => {
    switch (startTimeZone) {
      case StartTimeZone.AMSTERDAM:
        return Locale.translate(Translation.TC_START_TIME_ZONE_AMSTERDAM)
      case StartTimeZone.LONDON:
        return Locale.translate(Translation.TC_START_TIME_ZONE_LONDON)
      case StartTimeZone.NEW_YORK:
        return Locale.translate(Translation.TC_START_TIME_ZONE_NEW_YORK)
      case StartTimeZone.TOKYO:
        return Locale.translate(Translation.TC_START_TIME_ZONE_TOKYO)
      default:
        throw new Error(`Unhandled Start Time Zone ${[startTimeZone]}`)
    }
  }
}

export const IssueHelper: DataHelper<Issue> = {
  getId: (issue: Issue) => issue,
  getName: (issue: Issue) => {
    switch (issue) {
      case Issue.ISSUE_OOO2S:
        return 'ooo2s'
      case Issue.ISSUE_OO2S:
        return 'oo2s'
      case Issue.ISSUE_O2S:
        return 'o2s'
      case Issue.ISSUE_2YR:
        return '2YR'
      case Issue.ISSUE_WI2S:
        return 'WI2s'
      case Issue.ISSUE_OOO3S:
        return 'ooo3s'
      case Issue.ISSUE_OO3S:
        return 'oo3s'
      case Issue.ISSUE_O3S:
        return 'o3s'
      case Issue.ISSUE_3YR:
        return '3YR'
      case Issue.ISSUE_WI3S:
        return 'WI3s'
      case Issue.ISSUE_OOO5S:
        return 'ooo5s'
      case Issue.ISSUE_OO5S:
        return 'oo5s'
      case Issue.ISSUE_O5S:
        return 'o5s'
      case Issue.ISSUE_5YR:
        return '5YR'
      case Issue.ISSUE_WI5S:
        return 'WI5s'
      case Issue.ISSUE_OOO7S:
        return 'ooo7s'
      case Issue.ISSUE_OO7S:
        return 'oo7s'
      case Issue.ISSUE_O7S:
        return 'o7s'
      case Issue.ISSUE_7YR:
        return '7YR'
      case Issue.ISSUE_WI7S:
        return 'WI7s'
      case Issue.ISSUE_OOO10S:
        return 'ooo10s'
      case Issue.ISSUE_OO10S:
        return 'oo10s'
      case Issue.ISSUE_O10S:
        return 'o10s'
      case Issue.ISSUE_10YR:
        return '10YR'
      case Issue.ISSUE_WI10S:
        return 'WI10s'
      case Issue.ISSUE_OOO20S:
        return 'ooo20s'
      case Issue.ISSUE_OO20S:
        return 'oo20s'
      case Issue.ISSUE_O20S:
        return 'o20s'
      case Issue.ISSUE_20YR:
        return '20YR'
      case Issue.ISSUE_WI20S:
        return 'WI20s'
      case Issue.ISSUE_OOO30S:
        return 'ooo30s'
      case Issue.ISSUE_OO30S:
        return 'oo30s'
      case Issue.ISSUE_O30S:
        return 'o30s'
      case Issue.ISSUE_30YR:
        return '30YR'
      case Issue.ISSUE_WI30S:
        return 'WI30s'
      case Issue.ISSUE_1MO:
        return '1MO'
      case Issue.ISSUE_2MO:
        return '2MO'
      case Issue.ISSUE_3MO:
        return '3MO'
      case Issue.ISSUE_4MO:
        return '4MO'
      case Issue.ISSUE_6MO:
        return '6MO'
      case Issue.ISSUE_1YR:
        return '1YR'
      case Issue.ISSUE_CTD:
        return 'CTD'
      default:
        throw new Error(`Unhandled Start Time Zone ${[issue]}`)
    }
  }
}
